import { useState, useCallback } from 'react';
import { useInjection } from '../../../di_container/InjectionProvider';
import { Common } from '../../repository/dto';
import { Utils } from '../../../common/helper';
import { useClinicUser } from '../clinic/ClinicUserHooks';
import { useTranslation } from 'react-i18next';
import { Job } from '../../firestore_model';
import { pagination } from '../../../common/constants/Constants';
export const useJobOffer = () => {
    const columns = [
        { id: 'content', label: '業務内容', minWidth: 150 },
        { id: 'date', label: '日付', minWidth: 80 },
        { id: 'time', label: '時間', minWidth: 100 },
        { id: 'reward', label: '報酬', minWidth: 60, align: 'right', format: (val) => val.toLocaleString() },
        { id: 'type', label: '雇用形態', minWidth: 80 },
        { id: 'count', label: '応募数', minWidth: 50, align: 'right', format: (val) => val.toLocaleString() },
        { id: 'deadline', label: '締切残り時間', minWidth: 80 },
        { id: 'status', label: '状況', minWidth: 50 },
        { id: 'copyCreate', label: '', minWidth: 90 },
    ];
    const { container } = useInjection();
    const { jobClinic } = useClinicUser();
    const { t } = useTranslation();
    const [loading, setLoading] = useState(false);
    const [registeredJobOfferId, setRegisteredJobOfferId] = useState(undefined);
    const uploadFile = async (id, file, name, path) => {
        const storageRepository = container.resolve('CloudStorageRepository');
        // TODO このメタデータの型はどこかに引っ越し予定
        const metadata = {
            id: id,
            name: name,
            filename: file.name,
        };
        const result = await storageRepository.upload(file, name, path, metadata);
        const reqFile = new Common.StorageFile();
        reqFile.url = result.url;
        reqFile.metadata = metadata;
        reqFile.path = result.path;
        reqFile.mimeType = 'contentType' in result.metadata ? result.metadata['contentType'] : null;
        return reqFile;
    };
    const registerJobOffer = useCallback(async (data) => {
        setLoading(true);
        let jobOfferId = registeredJobOfferId;
        try {
            if (Utils.isNull(data.thumbnails) && Utils.isNull(data.thumbnailTemplate)) {
                throw new Error(t('写真を登録してください。'));
            }
            const imoryRepository = container.resolve('ImoryApiRepository');
            if (Utils.isNull(jobOfferId)) {
                const response = await imoryRepository.saveJobOffer(data.jobOffer);
                console.log('response.', response);
                jobOfferId = response.data.jobOfferId;
                // JobOffer作成後に再度JobOfferを作らないようstateで持たせる
                setRegisteredJobOfferId(jobOfferId);
            }
            console.log('saveJobOffer Done.', jobOfferId);
            if (jobOfferId === undefined || jobClinic === undefined) {
                throw new Error(t('システムエラー'));
            }
            let thumbnails = null;
            let documents = null;
            // テンプレートからサムネイルを選択した場合はファイルアップロードしない
            if (data.thumbnailTemplate) {
                for (const val of data.thumbnailTemplate) {
                    if (thumbnails === null) {
                        thumbnails = [val];
                    }
                    else {
                        thumbnails.push(val);
                    }
                }
            }
            else {
                if (Utils.isNotNull(data.thumbnails)) {
                    const path = jobClinic.jobOfferThumnailsPath(jobOfferId);
                    for (const [index, item] of data.thumbnails.entries()) {
                        const thumbnail = await uploadFile(`${index}`, item, Utils.getRandomString(), path);
                        if (thumbnails === null) {
                            thumbnails = [thumbnail];
                        }
                        else {
                            thumbnails.push(thumbnail);
                        }
                    }
                }
            }
            if (Utils.isNotNull(data.documents)) {
                const path = jobClinic.jobOfferDocumentsPath(jobOfferId);
                for (const [index, item] of data.documents.entries()) {
                    const document = await uploadFile(`${index}`, item, Utils.getRandomString(), path);
                    if (documents === null) {
                        documents = [document];
                    }
                    else {
                        documents.push(document);
                    }
                }
            }
            console.log('uploadFiles Done.');
            await imoryRepository.registerJobOffer({
                jobOfferId: jobOfferId,
                thumbnails: thumbnails,
                documents: documents,
            });
            console.log('registerJobOffer Done.');
            setLoading(false);
        }
        catch (e) {
            console.error('error', e);
            setLoading(false);
            throw e;
        }
    }, [jobClinic, registeredJobOfferId]);
    const updateRecruitment = useCallback(async (jobOfferId, value) => {
        setLoading(true);
        try {
            const imoryRepository = container.resolve('ImoryApiRepository');
            await imoryRepository.updateRecruitment(jobOfferId, value);
            setLoading(false);
        }
        catch (e) {
            console.error('error', e);
            setLoading(false);
            throw e;
        }
    }, []);
    const loadJobOfferList = useCallback(async (lastDocument) => {
        setLoading(true);
        try {
            if (Utils.isNull(jobClinic)) {
                throw new Error(t('システムエラー'));
            }
            const firestoreRepository = container.resolve('FirestoreRepository');
            const result = await firestoreRepository.loadDocuments({
                path: jobClinic.jobOffers.collectionReference.path,
                isGroup: false,
                limit: pagination.limit,
                startAfter: lastDocument,
                orderBy: {
                    fieldPath: 'createdAt',
                    directionStr: 'desc',
                },
            });
            setLoading(false);
            return {
                data: result.docs.map((item) => Job.JobOffer.fromSnapshot(item)),
                lastDocument: result.lastDocument,
            };
        }
        catch (e) {
            setLoading(false);
            throw e;
        }
    }, [jobClinic]);
    const loadJobOffer = useCallback(async (jobClinic, jobOfferId) => {
        setLoading(true);
        try {
            const firestoreRepository = container.resolve('FirestoreRepository');
            const result = await firestoreRepository.loadDocuments({
                path: jobClinic.jobOffers.collectionReference.path,
                isGroup: false,
                whereList: [{ fieldPath: 'jobOfferId', opStr: '==', value: jobOfferId }],
                limit: pagination.limit,
            });
            setLoading(false);
            return {
                data: result.docs.map((item) => Job.JobOffer.fromSnapshot(item)),
            };
        }
        catch (e) {
            setLoading(false);
            throw e;
        }
    }, [jobClinic]);
    return {
        columns,
        loading,
        registeredJobOfferId,
        setRegisteredJobOfferId,
        loadJobOfferList,
        loadJobOffer,
        registerJobOffer,
        updateRecruitment,
    };
};
